import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import LoadingOverlay from '../loadingOverlay/LoadingOverlay';
import StripoEditor from '../stripoEditor/StripoEditor';
import Setup from './views/Setup';
import Publish from './views/Publish';
import useSnackbar from '../hooks/useSnackbar';
import useApi from '../hooks/useApi';
import { useTheme } from '@mui/material/styles';
import { HubConnectionState } from '@microsoft/signalr';
import ContentInUseDialog from './dialogs/ContentInUseDialog';
import useEditorHubConnection from './hooks/useEditorHubConnection';
import AssignQRCode from './views/AssignQRCode';

const drawerWidth = 300;

const steps = [
    { name: 'Editor', description: 'Create your content' },
    { name: 'Setup', description: 'Set your page title and choose topics' },
    { name: 'QR Code', description: 'Assign your page to a QR Code' },
    { name: 'Final Checks', description: 'Last chance to right any wrongs...' }
];

const Page = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const theme = useTheme();
    const { showSnackbar } = useSnackbar();
    const { handleGet, handlePut, handleDelete } = useApi();
    const { connection, usersEditing, setUsersEditing } = useEditorHubConnection();
    const [isLoading, setIsLoading] = useState(true);
    const [step, setStep] = useState(0);
    const [previewHtml, setPreviewHtml] = useState('');
    const [page, setPage] = useState(null);
    const [qrCodeContent, setQRCodeContent] = useState(null);

    const titleBarDepth = theme.zIndex.drawer + 1;

    const handleLoadPage = async (specificLoad = null) => {
        const response = await handleGet(`pages/${id}`);

        if (!response.ok) {
            showSnackbar('Error fetching Page', 'error');
            navigate('/editor');
            return;
        }

        if (specificLoad === 'topics') {
            const { topics } = await response.json();

            setPage({
                ...page,
                topics
            });
        }
        else if (specificLoad === 'qrcodes') {
            const { qrCodes } = await response.json();

            setPage({
                ...page,
                qrCodes
            });
        }
        else {
            const data = await response.json();
            setPage(data);
        }
    };

    const handleLoad = async () => {
        await handleLoadPage();
        setIsLoading(false);
    };

    const handleUpdatePage = async () => {
        const response = await handlePut(`pages/${id}`, page);

        if (!response.ok) {
            showSnackbar('Error updating Page', 'error');
            return;
        }

        step === 0 && showSnackbar('Changes saved', 'success');
    };

    const handleUpdateEmail = props => {
        setPage({
            ...page,
            ...props,
            compiledHtml: props.html
        });
    };

    const handleCompleteEdit = previewHtml => {
        setPage({
            ...page,
            compiledHtml: previewHtml
        });

        setPreviewHtml(previewHtml);
        setStep(1);
    };

    const handleCompleteSetup = async (newPage, step) => {
        setPage({
            ...page,
            ...newPage
        });

        setStep(step);
    };

    const requestEditorAccess = async () => {
        try {
            await connection.start().then(() => {
                connection.invoke('RequestEditorAccess', parseInt(id));
            }).then(() => {
                connection.invoke('GetEditorsInUseByEmail', parseInt(id));
            });
            connection.on('GetUsersEditing', editorsInUse => {
                setUsersEditing(editorsInUse);
            });
        }
        catch (err) {
            console.error(err);
        }
    };

    const handleDeleteQRCodeContent = async (qrCodeId, qrCodeContentId) => {
        const response = await handleDelete(`qrcodes/${qrCodeId}/content/${qrCodeContentId}`);

        if (!response.ok) {
            showSnackbar('Error deleting QR Code association', 'error');
            return;
        }

        handleLoadPage('qrcodes');
    };

    useEffect(() => {
        if (isLoading) {
            return;
        }

        handleUpdatePage();
    }, [page]);

    useEffect(() => {
        if (connection && connection.state !== HubConnectionState.Connected) {
            requestEditorAccess();
        }
    }, [connection]);

    useEffect(() => {
        handleLoad();
    }, []);

    if (isLoading) {
        return (
            <LoadingOverlay />
        );
    }

    return (
        <>
            {!!usersEditing.length && <ContentInUseDialog usersEditing={usersEditing} type="page" />}
            {step === 0 ? (
                <StripoEditor
                    email={page}
                    onUpdateEmail={handleUpdateEmail}
                    onNext={handleCompleteEdit}
                    type="page"
                />
            ) : (
                <>
                    {step === 1 && (
                        <Setup
                            email={page}
                            onUpdateEmail={props => setPage({ ...page, ...props })}
                            onNext={campaign => handleCompleteSetup(campaign, 2)}
                            onBack={campaign => handleCompleteSetup(campaign, 0)}
                            onFetchTopics={() => handleLoadPage('topics')}
                            drawerWidth={drawerWidth}
                            steps={steps}
                            step={step}
                            campaign={page}
                            isProofSent={false}
                            audienceName={null}
                            titleBarDepth={titleBarDepth}
                            setStep={setStep}
                            type="page"
                        />
                    )}
                    {step === 2 && (
                        <AssignQRCode
                            qrCodeContent={qrCodeContent}
                            setQRCodeContent={setQRCodeContent}
                            onNext={() => setStep(3)}
                            onBack={() => setStep(1)}
                            drawerWidth={drawerWidth}
                            steps={steps}
                            step={step}
                            campaign={page}
                            isProofSent={false}
                            audienceName={null}
                            titleBarDepth={titleBarDepth}
                            setStep={setStep}
                            onDeleteQRCodeContent={handleDeleteQRCodeContent}
                            contentType="Landing Page"
                        />
                    )}
                    {step === 3 && (
                        <Publish
                            qrCodeContent={qrCodeContent}
                            page={page}
                            onBack={() => setStep(2)}
                            previewHtml={previewHtml}
                            steps={steps}
                            step={step}
                            setStep={setStep}
                            drawerWidth={drawerWidth}
                            titleBarDepth={titleBarDepth}
                        />
                    )}
                </>
            )}
        </>
    );
};

export default Page;